/* eslint max-lines: ["error", { "max": 1000 }] */
import { yupResolver } from '@hookform/resolvers/yup';
import { useQueryClient } from '@tanstack/react-query';
import { FamilyUnit, InsuredSubtype, PossibleFipsCode } from '@zorro/clients';
import {
  getFieldRequirementsFromClassDefinitions,
  insuredMinDateOfBirth,
  logger,
} from '@zorro/common-utils';
import { formatDateISO, getNow, parseDateISO } from '@zorro/shared/formatters';
import { YesNo, calculateFamilyUnit } from '@zorro/types';
import {
  useForm,
  useLoadingOverlay,
  useMonolithMutation,
  useMonolithQuery,
} from '@zorro/web/ui-utils';
import { useEffect, useState } from 'react';

import { usePageLoadAnalyticsEvent } from '../Analytics';
import {
  InsuredDependentsFormFields,
  getDependentSchema,
  getDependentsFormSchema,
} from './DependentsFormInputs';
import {
  InsuredEmployeeFormFields,
  getEmployeeFormSchema,
} from './EmployeeFormInputs';
import {
  InsuredFormValidity,
  SpouseStatus,
  UseInsuredFormReturnType,
  getDefaultInsuredFormFields,
  getInvalidTabs,
  mapInsuredDtoToBaseInsuredFormFields,
  relaxResidentialAddress,
  validateAllAddressesAndGetFailedTabs,
} from './InsuredFormUtils';
import {
  InsuredSpouseFormFields,
  getSpouseInsuredSchema,
} from './SpouseFormInputs';
import { useFollowEmployeeAddress } from './useFollowEmployeeAddress';

type InsuredFormPerson = {
  dateOfBirth: Date;
  isPregnant?: YesNo | null;
  isSmoker?: YesNo | null;
  residentialAddress?: string;
};

const toCreateInsuredPerson = <T extends InsuredFormPerson>(
  person: T,
  employeeId: string,
  employerId: string
) => ({
  ...person,
  dateOfBirth: formatDateISO(person.dateOfBirth),
  isPregnant: person.isPregnant === YesNo.YES,
  isSmoker: person.isSmoker === YesNo.YES,
  residentialAddress: person.residentialAddress,
  employeeId,
  employerId,
});

const toUpdateInsuredPerson = <T extends InsuredFormPerson>(
  person: T,
  id: string
) => ({
  ...person,
  dateOfBirth: formatDateISO(person.dateOfBirth),
  isPregnant: person.isPregnant === YesNo.YES,
  isSmoker: person.isSmoker === YesNo.YES,
  residentialAddress: person.residentialAddress,
  id,
});

function buildInsuredUpsertPayload<TEmployee extends InsuredFormPerson>(
  employeeWithExtraFields: TEmployee,
  insuredPeople: {
    employee: { id: string };
    spouse?: { id: string } | null;
    dependents: { id: string }[];
  },
  spouseStatus: SpouseStatus,
  resolvedSpouse: InsuredSpouseFormFields,
  resolvedDependents: InsuredDependentsFormFields['dependents'],
  employeeId: string,
  employerId: string
) {
  const createSpouse =
    spouseStatus.isSpouseActive && !insuredPeople.spouse
      ? toCreateInsuredPerson(resolvedSpouse, employeeId, employerId)
      : undefined;

  const updateSpouse =
    spouseStatus.isSpouseActive && insuredPeople.spouse
      ? toUpdateInsuredPerson(resolvedSpouse, insuredPeople.spouse.id)
      : undefined;

  const deletedSpouseId =
    !spouseStatus.isSpouseActive && insuredPeople.spouse
      ? insuredPeople.spouse.id
      : undefined;

  const createDependents = resolvedDependents
    .filter((dep) => !dep.insuredId)
    .map(({ insuredId: _insuredId, ...dep }) =>
      toCreateInsuredPerson(dep, employeeId, employerId)
    );

  const updateDependents = resolvedDependents
    .filter((dep): dep is typeof dep & { insuredId: string } =>
      Boolean(dep.insuredId)
    )
    .map(({ insuredId, ...dep }) => toUpdateInsuredPerson(dep, insuredId));

  const currentDepIds = new Set(
    resolvedDependents.map((dep) => dep.insuredId).filter(Boolean)
  );
  const deletedDependentIds = insuredPeople.dependents
    .filter((dep) => !currentDepIds.has(dep.id))
    .map((dep) => dep.id);

  return {
    insuredsToCreate: {
      spouse: createSpouse,
      dependents: createDependents,
    },
    insuredsToUpdate: {
      employee: toUpdateInsuredPerson(
        employeeWithExtraFields,
        insuredPeople.employee.id
      ),
      spouse: updateSpouse,
      dependents: updateDependents,
    },
    insuredsToDeleteIds: [
      ...(deletedSpouseId ? [deletedSpouseId] : []),
      ...deletedDependentIds,
    ],
  };
}

function buildEmployeeWithExtraFields<T extends InsuredFormPerson>(
  employee: T,
  mailingAddress: string | undefined,
  personalEmail: string | undefined,
  phone: string | null | undefined,
  resolvedFipsCode: string | undefined
) {
  return {
    ...employee,
    mailingAddress,
    personalEmail: personalEmail || null,
    phoneNumber: phone,
    fipsCode: resolvedFipsCode || null,
  };
}

type FipsCodesState = {
  employee: PossibleFipsCode[];
  spouse: PossibleFipsCode[];
  dependents: Record<number, PossibleFipsCode[]>;
};

type Props = {
  employeeId: string;
  employerId: string;
  onboardingPeriodId: string;
  isFinalizationMode: boolean;
  isEmployeeMode: boolean;
  withAnticipatedCareLevel?: boolean;
  withResidentialAddress?: boolean;
  // A form that hides the address inputs but saves an address collected
  // elsewhere still has to resolve every household member's county.
  withAddressValidation?: boolean;
  onSuccess?: (newFamilyUnit: FamilyUnit) => Promise<void>;
  shouldReloadPage?: boolean;
  pageLoadEventName?: string;
  shouldShowLoadingOverlay?: boolean;
};

export const useEnrollmentInsuredForm = ({
  onboardingPeriodId,
  employeeId,
  employerId,
  isFinalizationMode,
  isEmployeeMode,
  withAnticipatedCareLevel = false,
  withResidentialAddress = true,
  withAddressValidation = withResidentialAddress,
  pageLoadEventName,
  onSuccess,
  shouldReloadPage = true,
  shouldShowLoadingOverlay = true,
  // eslint-disable-next-line sonarjs/cognitive-complexity
}: Props): UseInsuredFormReturnType => {
  'use no memo';
  const { startLoading, stopLoading } = useLoadingOverlay();
  const queryClient = useQueryClient();

  const [allPossibleFipsCodes, setAllPossibleFipsCodes] =
    useState<FipsCodesState>({
      employee: [],
      spouse: [],
      dependents: {},
    });

  const setEmployeeFipsCodes = (codes: PossibleFipsCode[]) =>
    setAllPossibleFipsCodes((prev) => ({ ...prev, employee: codes }));
  const setSpouseFipsCodes = (codes: PossibleFipsCode[]) =>
    setAllPossibleFipsCodes((prev) => ({ ...prev, spouse: codes }));
  const setDependentFipsCodes = (index: number, codes: PossibleFipsCode[]) =>
    setAllPossibleFipsCodes((prev) => ({
      ...prev,
      dependents: { ...prev.dependents, [index]: codes },
    }));

  const { mutate: mutateInsured } = useMonolithMutation({
    method: 'insuredControllerUpsertInsuredForPeriod',
    shouldReloadPage,
    shouldInvalidateQueries: false,
    shouldShowLoadingOverlay,
  });

  const [openedTabs, setOpenedTabs] = useState<string[]>(['EMPLOYEE']);
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);

  const [spouseStatus, setSpouseStatus] = useState<SpouseStatus>({
    spouseWasRemoved: false,
    isSpouseActive: false,
  });

  const { data: me } = useMonolithQuery({
    method: 'employeesControllerFindOne',
    params: [employeeId],
  });

  const { data: onboardingPeriods = [] } = useMonolithQuery({
    method: 'onboardingPeriodsControllerFindMany',
    params: [employeeId],
  });

  const onboardingPeriod = onboardingPeriods.find(
    ({ id }) => onboardingPeriodId === id
  );
  const lastSubmittedEnrollmentPeriodId =
    onboardingPeriods.length > 0 &&
    onboardingPeriods.reduce((prev, current) => {
      return parseDateISO(prev.effectiveFrom).isAfter(
        parseDateISO(current.effectiveFrom)
      )
        ? prev
        : current;
    })?.id;

  const { data: insured } = useMonolithQuery({
    method: 'insuredControllerFindInsuredForPeriod',
    params: [onboardingPeriodId],
  });
  const { data: lastSubmittedInsured } = useMonolithQuery({
    method: 'insuredControllerFindInsuredForPeriod',
    params: Boolean(insured) &&
      lastSubmittedEnrollmentPeriodId && [lastSubmittedEnrollmentPeriodId],
  });

  const insuredPeople = insured ?? lastSubmittedInsured;
  const employee = insuredPeople?.employee;

  const targetPlanYear = onboardingPeriod?.effectiveFrom
    ? parseDateISO(onboardingPeriod.effectiveFrom).toDate().getFullYear()
    : undefined;

  const { data: classDefinitions } = useMonolithQuery({
    method: 'allowanceModelControllerGetClassDefinitionByYear',
    params: targetPlanYear && [employerId, targetPlanYear],
  });

  const requiredClassCategories = classDefinitions?.items
    ? getFieldRequirementsFromClassDefinitions(classDefinitions.items)
    : undefined;

  const employeeForm = useForm<InsuredEmployeeFormFields>({
    mode: 'all',
    resolver: yupResolver(
      relaxResidentialAddress(
        getEmployeeFormSchema(
          isFinalizationMode,
          withAnticipatedCareLevel,
          requiredClassCategories
        ),
        withResidentialAddress
      )
    ),
    defaultValues:
      insuredPeople && me
        ? ({
            ...mapInsuredDtoToBaseInsuredFormFields(
              withAnticipatedCareLevel,
              employee || me
            ),
            email: employee?.companyEmail || me.email,
            phone: employee?.phoneNumber || me.phone || '',
            personalEmail: employee?.personalEmail || '',
            residentialAddress:
              employee?.residentialAddress || me.address || '',
            isMailingAddressSameAsResidentialAddress:
              employee?.residentialAddress && employee?.mailingAddress
                ? employee.residentialAddress === employee.mailingAddress
                : true,
            mailingAddress: employee?.mailingAddress || '',
            citizenshipStatus: employee?.citizenshipStatus || null,
            maritalStatus: employee?.maritalStatus || null,
            employmentType: employee?.employmentType || null,
            wageType: employee?.wageType || null,
            // Left empty on purpose: only a county the user picks from the
            // disambiguation dropdown is sent. Seeding the stored one made it
            // round-trip back as explicitlyProvidedFips, which stops the server
            // re-resolving the county from the address.
            fipsCode: undefined,
            customClassCategory: employee?.customClassCategory || null,
          } satisfies InsuredEmployeeFormFields)
        : undefined,
  });

  const isDomesticPartnerAllowed =
    onboardingPeriod?.enrollmentIsDomesticPartnerAllowed ?? true;

  const spouseForm = useForm<InsuredSpouseFormFields>({
    mode: 'all',
    resolver: yupResolver(
      relaxResidentialAddress(
        getSpouseInsuredSchema(isFinalizationMode, withAnticipatedCareLevel),
        withResidentialAddress
      )
    ),
    defaultValues:
      insuredPeople?.spouse && !spouseStatus.spouseWasRemoved
        ? {
            ...mapInsuredDtoToBaseInsuredFormFields(
              withAnticipatedCareLevel,
              insuredPeople.spouse
            ),
            subtype: insuredPeople.spouse.subtype || undefined,
          }
        : {
            ...getDefaultInsuredFormFields(employee?.residentialAddress),
            subtype: isDomesticPartnerAllowed
              ? undefined
              : InsuredSubtype.SPOUSE,
          },
  });

  const cancelSpouse = () => {
    if (insuredPeople?.spouse) {
      spouseForm.reset({
        ...mapInsuredDtoToBaseInsuredFormFields(
          withAnticipatedCareLevel,
          insuredPeople.spouse
        ),
        subtype: insuredPeople.spouse.subtype || undefined,
      });
      setSpouseStatus({
        isSpouseActive: true,
        spouseWasRemoved: false,
      });
    } else {
      spouseForm.reset();
    }
  };

  const effectiveFrom = onboardingPeriod?.effectiveFrom
    ? parseDateISO(onboardingPeriod.effectiveFrom)
    : getNow();

  const dependentSchema = relaxResidentialAddress(
    getDependentSchema(
      isFinalizationMode,
      withAnticipatedCareLevel,
      insuredMinDateOfBirth(true, effectiveFrom),
      true
    ),
    withResidentialAddress
  );

  const dependentsForm = useForm<InsuredDependentsFormFields>({
    mode: 'all',
    defaultValues: {
      dependents:
        insuredPeople?.dependents && insuredPeople.dependents.length > 0
          ? insuredPeople.dependents.map((dependent) => ({
              ...mapInsuredDtoToBaseInsuredFormFields(
                withAnticipatedCareLevel,
                dependent
              ),
              subtype: dependent.subtype || undefined,
              insuredId: dependent.id,
              isDisabled: Boolean(dependent.isDisabled),
            }))
          : [],
    },
    resolver: yupResolver(getDependentsFormSchema(dependentSchema)),
  });

  // Read on every render to keep react-hook-form revalidating each sub-form.
  // See InsuredFormValidity for why a later-mounting section is not enough.
  const formValidity: InsuredFormValidity = {
    isEmployeeValid: employeeForm.formState.isValid,
    isSpouseValid: spouseForm.formState.isValid,
    areDependentsValid: dependentsForm.formState.isValid,
  };

  const hasAnyDependents = dependentsForm.watch('dependents').length > 0;
  const employeeAddress = employeeForm.watch('residentialAddress');

  useFollowEmployeeAddress({
    employeeAddress,
    spouseForm,
    dependentsForm,
    clearSpousePossibleFipsCodes: () => setSpouseFipsCodes([]),
    clearDependentPossibleFipsCodes: (index) =>
      setDependentFipsCodes(index, []),
    isEnabled: isEmployeeMode,
  });

  useEffect(() => {
    setSpouseStatus((prev) => {
      const isSpouseActive = Boolean(insured?.spouse);
      if (prev.isSpouseActive === isSpouseActive) {
        return prev;
      }
      return {
        ...prev,
        isSpouseActive,
      };
    });
  }, [insured, setSpouseStatus]);

  usePageLoadAnalyticsEvent({
    eventName: pageLoadEventName,
    payload: {
      onboarding_period_id: onboardingPeriodId,
      family_unit: insuredPeople
        ? calculateFamilyUnit(insuredPeople)
        : undefined,
    },
    isEnabled: Boolean(onboardingPeriodId && insuredPeople),
  });

  const conditionalStartLoading = shouldShowLoadingOverlay
    ? startLoading
    : () => {};
  const conditionalStopLoading = shouldShowLoadingOverlay
    ? stopLoading
    : () => {};

  return {
    employeeForm,
    spouseForm,
    dependentsForm,
    formValidity,
    hasAttemptedSubmit,
    dependentSchema,
    targetEnrollmentDate: effectiveFrom,
    hasAnyDependents,
    employeeZipCode: employee?.zipCode,
    employeeFipsCode: employee?.fipsCode,
    spouseStatus,
    setSpouseStatus,
    cancelSpouse,
    openedTabs,
    setOpenedTabs,
    isDomesticPartnerAllowed,
    possibleFipsCodes: allPossibleFipsCodes.employee,
    setPossibleFipsCodes: setEmployeeFipsCodes,
    spousePossibleFipsCodes: allPossibleFipsCodes.spouse,
    setSpousePossibleFipsCodes: setSpouseFipsCodes,
    dependentPossibleFipsCodes: allPossibleFipsCodes.dependents,
    setDependentPossibleFipsCodes: setDependentFipsCodes,
    withResidentialAddress,
    onSubmit: async (): Promise<boolean> => {
      if (!onboardingPeriod || !insuredPeople) return false;

      setHasAttemptedSubmit(true);
      conditionalStartLoading();
      const validationResults = await Promise.all([
        employeeForm.trigger(),
        spouseStatus.isSpouseActive ? spouseForm.trigger() : true,
        hasAnyDependents ? dependentsForm.trigger() : true,
      ]);

      if (validationResults.some((result) => !result)) {
        setOpenedTabs(
          getInvalidTabs(
            employeeForm.formState.isValid,
            spouseStatus.isSpouseActive,
            spouseForm.formState.isValid,
            hasAnyDependents,
            dependentsForm.getValues().dependents.length,
            (index) =>
              Boolean(dependentsForm.formState.errors?.dependents?.[index])
          )
        );
        conditionalStopLoading();
        return false;
      }

      const {
        email,
        phone,
        personalEmail,
        isMailingAddressSameAsResidentialAddress,
        fipsCode,
        ...employee
      } = employeeForm.getValues();
      const spouse = spouseForm.getValues();
      const { dependents } = dependentsForm.getValues();

      const failedTabs = withAddressValidation
        ? await validateAllAddressesAndGetFailedTabs(
            employee,
            fipsCode,
            (field, error) => employeeForm.setError(field, error),
            (code) => employeeForm.setValue('fipsCode', code),
            setEmployeeFipsCodes,
            spouse,
            spouseStatus,
            (field, error) => spouseForm.setError(field, error),
            (code) => spouseForm.setValue('fipsCode', code),
            setSpouseFipsCodes,
            dependents,
            (index, field, error) =>
              dependentsForm.setError(`dependents.${index}.${field}`, error),
            (index, code) =>
              dependentsForm.setValue(`dependents.${index}.fipsCode`, code),
            (index, codes) => setDependentFipsCodes(index, codes),
            conditionalStopLoading,
            insuredPeople,
            (address) => employeeForm.setValue('residentialAddress', address),
            (address) => spouseForm.setValue('residentialAddress', address),
            (index, address) =>
              dependentsForm.setValue(
                `dependents.${index}.residentialAddress`,
                address
              )
          )
        : [];

      if (failedTabs.length > 0) {
        setOpenedTabs(failedTabs);
        return false;
      }

      // Re-read form values to pick up what validation wrote back: the
      // canonical address, and any fipsCode set during disambiguation
      const {
        fipsCode: resolvedFipsCode,
        residentialAddress: resolvedResidentialAddress,
      } = employeeForm.getValues();
      const resolvedEmployee = {
        ...employee,
        residentialAddress: resolvedResidentialAddress,
      };
      const resolvedSpouse = spouseForm.getValues();
      const { dependents: resolvedDependents } = dependentsForm.getValues();

      const mailingAddress = isMailingAddressSameAsResidentialAddress
        ? resolvedEmployee.residentialAddress
        : resolvedEmployee.mailingAddress;

      let result = false;
      try {
        const employeeWithExtraFields = buildEmployeeWithExtraFields(
          resolvedEmployee,
          mailingAddress,
          personalEmail,
          phone,
          resolvedFipsCode
        );

        const payload = buildInsuredUpsertPayload(
          employeeWithExtraFields,
          insuredPeople,
          spouseStatus,
          resolvedSpouse,
          resolvedDependents,
          employeeId,
          employerId
        );

        await mutateInsured([onboardingPeriodId, payload]);

        setTimeout(async () => {
          await queryClient.invalidateQueries();
        }, 0);

        setSpouseStatus((prev) => ({
          ...prev,
          spouseWasRemoved: false,
        }));

        await onSuccess?.(
          calculateFamilyUnit({
            employee: {
              ...employee,
              dateOfBirth: formatDateISO(employee.dateOfBirth),
            },
            spouse: spouseStatus.isSpouseActive
              ? { ...spouse, dateOfBirth: formatDateISO(spouse.dateOfBirth) }
              : undefined,
            dependents: dependents.map((dep) => ({
              ...dep,
              dateOfBirth: formatDateISO(dep.dateOfBirth),
            })),
          })
        );
        result = true;
      } catch (error) {
        logger.error(error);
      } finally {
        conditionalStopLoading();
      }
      return result;
    },
  };
};
